import React, { useEffect, useMemo, useState, memo } from "react";
import {
  Container,
  Typography,
  Button,
  Box,
  TextField,
  Paper,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  Tabs,
  Tab,
} from "@mui/material";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { TierData, tierIdToName } from "../../util/PatreonUtil";
import { useAppContext } from "../../context/AppContext";
import { sendAxiosPost } from "../../util/ServerUtil";
import { TabContext, TabPanel } from "@mui/lab";
import { toast } from "react-toastify";
import { useModal } from "../../context/ModalContext";
import CopyableText from "../../components/CopyableText";
import { Link } from "react-router-dom";

type AdminSetClaimsResponse =
  | { success: true; uid: string; claims: Record<string, any> }
  | { success: false; error?: string }
  | any;

interface UserData {
  firebaseUid: string;
  firebaseEmail: string;
  patreonId: string;
  tierId: string;
  patreonEmail: string;
  patronStatus: string;
  lifetimeSupport: string;
  fullName: string;
}

const columns: GridColDef[] = [
  { field: 'id', headerName: 'UID', width: 175, renderCell: (params) => <CopyableText value={params.value} /> },
  { field: 'firebaseEmail', headerName: 'Firebase Email', width: 175, renderCell: (params) => <CopyableText value={params.value} /> },
  { field: 'patreonId', headerName: 'Patreon ID' },
  { field: 'tierId', headerName: 'Tier', renderCell: (params) => {
      const tierName = tierIdToName(params.value);
      const tierInfo = TierData[params.value];
      const color = tierInfo?.fileAccess ? "green" : "red";
      return <span style={{ color }}>{tierName}</span>;
    },
  },
  { field: 'patreonEmail', headerName: 'Patreon Email', width: 175, renderCell: (params) => <CopyableText value={params.value} /> },
  { field: 'patronStatus', headerName: 'Patron Status' },
  { field: 'lifetimeSupport', headerName: 'Lifetime Support', type: 'number' },
  { field: 'fullName', headerName: 'Full Name' },
]

const UserManager: React.FC = () => {
  const { isAdmin } = useAppContext();

  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);

  const [userData, setUserData] = useState<UserData[] | null>(null);

  // target resolution inputs
  const [targetUid, setTargetUid] = useState("");
  const [targetPatreonId, setTargetPatreonId] = useState("");

  // editable claims
  const [patreonId, setPatreonId] = useState<string>("");
  const [tierId, setTierId] = useState<string>("");

  // behavior flags
  const [clearPatreonId, setClearPatreonId] = useState(false);

  // request state
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState<AdminSetClaimsResponse | null>(null);

  // display current server-returned claims
  const [serverClaims, setServerClaims] = useState<Record<string, any> | null>(null);
  const [resolvedUid, setResolvedUid] = useState<string | null>(null);

  const { showModal } = useModal();

  const [searchText, setSearchText] = useState("");
  const [appliedSearchText, setAppliedSearchText] = useState("");
  // Filter userData based on search
  const filteredUserData = useMemo(() => {
    if (!userData || !appliedSearchText.trim()) return userData;
    const search = appliedSearchText.toLowerCase();
    return userData.filter((user) =>
      Object.values(user).some((val) =>
        String(val).toLowerCase().includes(search)
      )
    );
  }, [userData, appliedSearchText]);

  const handleSearchClick = () => {
    setAppliedSearchText(searchText);
  };

  const handleSearchClear = () => {
    setSearchText("");
    setAppliedSearchText("");
  };

  useEffect(() => {
    document.title = "Admin | ipwd.club";
    setLoading(false);
  }, []);
  
  const handleUpdatePatreonData = () => {
      showModal({
          title: "Confirm Update Data",
          message: "Really?",
          onConfirm: () => {
            reallyHandleUpdatePatreonData();
          },
          onCancel: () => {
          },
      });
  }

  const reallyHandleUpdatePatreonData = async () => {
    sendAxiosPost("/api/updatePatreonData", null, true).then((response) => {
      if (response?.success) {
        toast.success("Patreon data updated successfully.");
      } else {
        toast.error("Failed to update Patreon data.");
      }
    });
  }

  const handleFetchPatreonData = async () => {
    const loadId = toast.loading("Fetching Patreon data...");
    sendAxiosPost("/api/fetchAllUserData", null, true).then((response) => {

      if (response?.success) {
        toast.update(loadId, { render: "Patreon data fetched successfully.", type: "success", isLoading: false, autoClose: 3000 });
        // build firebaseData and patreonData arrays
        const userDataArray: UserData[] = [];
        const usersResults: any[] = response.data.users || [];
        usersResults.forEach((u: any) => {
          const data: any = {
            id: u.uid,
            firebaseEmail: u.firebaseEmail,
            patreonId: u.patreonId || "",
          };
          if (u.patreonId && response.data.patreon[u.patreonId]) {
            const pData = response.data.patreon[u.patreonId];
            data.tierId = pData.tierId || "";
            data.patreonEmail = pData.patreonEmail || "";
            data.patronStatus = pData.patronStatus || "";
            data.lifetimeSupport = pData.lifetimeSupport || "";
            data.fullName = pData.fullName || "";
          }
          userDataArray.push(data);
        });
        setUserData(userDataArray);
      }
      else {
        toast.update(loadId, { render: "Failed to fetch Patreon data.", type: "error", isLoading: false, autoClose: 3000 });
      }
    });
  }

  const canSubmit = useMemo(() => {
    const hasTarget = targetUid.trim().length > 0 || targetPatreonId.trim().length > 0;
    const hasAnyClaimChange =
      clearPatreonId ||
      patreonId.trim().length > 0 ||
      tierId.trim().length > 0;
    return hasTarget && hasAnyClaimChange && !busy;
  }, [targetUid, targetPatreonId, clearPatreonId, patreonId, tierId, busy]);

  const buildClaimsPayload = () => {
    // Only send keys the admin intends to modify
    const claims: Record<string, string | null> = {};

    if (clearPatreonId) claims.patreonId = null;
    else if (patreonId.trim().length > 0) claims.patreonId = patreonId.trim();

    if (tierId.trim().length > 0) claims.tierId = tierId.trim();

    return claims;
  };

  const handleApply = async () => {
    setBusy(true);
    setLastResult(null);

    try {
      const payload: any = {
        claims: buildClaimsPayload(),
      };
      if (targetUid.trim()) payload.targetUid = targetUid.trim();
      else payload.targetPatreonId = targetPatreonId.trim();

      const resp = await sendAxiosPost("/api/admin/setUserClaims", payload, true);
      setLastResult(resp);

      if (resp?.success) {
        setResolvedUid(resp.uid);
        setServerClaims(resp.claims ?? null);

        // if you just set patreon/tier, you may want to keep the inputs in sync with returned state:
        if (resp.claims) {
          setPatreonId(resp.claims.patreonId ?? "");
          setTierId(resp.claims.tierId ?? "");
          setClearPatreonId(false);
        }
      }
    } catch (e: any) {
      setLastResult({ success: false, error: e?.message || "Unknown error" });
    } finally {
      setBusy(false);
    }
  };

  const handleReset = () => {
    setTargetUid("");
    setTargetPatreonId("");
    setPatreonId("");
    setTierId("");
    setClearPatreonId(false);
    setLastResult(null);
    setServerClaims(null);
    setResolvedUid(null);
  };

  if (!isAdmin) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h4">Access Denied</Typography>
        <Typography>You do not have permission to access this page.</Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h4">Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container
      sx={{ mt: 4, display: "flex", flexDirection: "column", alignItems: "stretch", gap: 2 }}
    >
      <Container sx={{ mb: 2 }}>
          <Link to="/admin/serverLogs" className="btn">Audit Logs</Link>
          <Link to="/admin/userManager" className="btn-active">User Manager</Link>
          <Link to="/admin/videoDataManager" className="btn">Video Data Manager</Link>
      </Container>
      <Typography variant="h4" gutterBottom>
        User Manager
      </Typography>

      <TabContext value={currentTab}>
        <Tabs value={currentTab} aria-label="User Manager Tabs" onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label="User Data" value={0} />
          <Tab label="User Editor" value={1} />
        </Tabs>
        <TabPanel value={0}>
          <Typography variant="h5">Force Update</Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={handleUpdatePatreonData}>
            Update User Data
          </Button><br/><br/>
          <Typography variant="h5">User Data</Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={handleFetchPatreonData}>
            Fetch User Data
          </Button>
          {
            filteredUserData ? 
            <>
            <br />
            <br />
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="Search users"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={handleSearchClick}
                sx={{ whiteSpace: "nowrap" }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleSearchClear}
                sx={{ whiteSpace: "nowrap" }}
              >
                Clear
              </Button>
            </Box>
            <DataGrid
              rows={filteredUserData}
              columns={columns}
              
              sx={{ border: 0 }}
            />
            </>
            : 
            <Typography sx={{ mt: 2 }}>No user data loaded.</Typography>
          }
        </TabPanel>
        <TabPanel value={1}>
          <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Target user
          </Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Firebase UID"
              value={targetUid}
              onChange={(e) => setTargetUid(e.target.value)}
              fullWidth
              helperText="Preferred. Paste the Firebase UID to modify."
              disabled={busy}
            />
            <TextField
              label="Patreon ID"
              value={targetPatreonId}
              onChange={(e) => setTargetPatreonId(e.target.value)}
              fullWidth
              helperText="Alternative. Resolves via users.patreonId."
              disabled={busy}
            />
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            If both are filled, the server will use <b>Firebase UID</b>.
          </Alert>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Data to set / clear
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Patreon ID"
                value={patreonId}
                onChange={(e) => setPatreonId(e.target.value)}
                fullWidth
                disabled={busy || clearPatreonId}
                helperText={clearPatreonId ? "Will be cleared" : "Leave blank to not change unless 'Clear' is checked"}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={clearPatreonId}
                    onChange={(e) => setClearPatreonId(e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Clear patreonId"
              />
            </Box>

            <FormControl fullWidth disabled={busy}>
              <InputLabel id="tierId-label">Patreon Tier</InputLabel>
              <Select
                labelId="tierId-label"
                label="tierId (custom claim)"
                value={tierId}
                onChange={(e: SelectChangeEvent) => setTierId(e.target.value)}
              >
                <MenuItem value="">
                  <em>— Don’t change —</em>
                </MenuItem>

                {Object.entries(TierData).map(([id, tier]) => (
                  <MenuItem key={id} value={id}>
                    {tier.name} ({id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: 2, mt: 2, alignItems: "center" }}>
            <Button variant="contained" onClick={handleApply} disabled={!canSubmit}>
              Apply
            </Button>

            <Button variant="outlined" onClick={handleReset} disabled={busy}>
              Reset
            </Button>

            {busy && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Updating…</Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {lastResult && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Result
            </Typography>

            {lastResult.success ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Updated user claims successfully.
                </Alert>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Resolved UID: <b>{resolvedUid}</b>
                </Typography>

                <Typography variant="subtitle2">Server-returned claims:</Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: "rgba(0,0,0,0.04)",
                    overflowX: "auto",
                    fontSize: 13,
                  }}
                >
                  {JSON.stringify(serverClaims, null, 2)}
                </Box>
              </>
            ) : (
              <Alert severity="error">
                {lastResult.error || "Request failed. Check server logs for details."}
              </Alert>
            )}
          </Paper>
        )}
        </TabPanel>
      </TabContext>
    </Container>
  );
};

export default memo(UserManager);
